import { Component, Inject } from '@angular/core';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CrmActivityRecordChangeBaseComponent } from './record-change-base.component';
import {
  StandardIds,
  CrmFieldService,
  CrmObjectService,
  CrmObjectDisplayService,
} from '../../../../../../shared-services';
import { ActivityCardComponent } from '../activity-card/activity-card.component';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { MatCardModule } from '@angular/material/card';
import { TranslationModule } from '../../../../../../i18n/translation-module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { SalespersonService, SalesSdk } from '@vendasta/sales';
import { ObjectType, CrmDependencies, CrmInjectionToken } from '../../../../../../tokens-and-interfaces';
import { TranslateForCrmObjectService } from '../../../../../../i18n/translate-for-crm-object/translate-for-crm-object.service';
import { ActivityAssociationService } from '../../../../activities';
import { CRMChangeLogApiService } from '@vendasta/crm';

@Component({
  selector: 'crm-activity-record-change-salesperson-updated',
  templateUrl: './record-change-base.component.html',
  styleUrls: ['./record-change.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCardModule,
    GalaxyPipesModule,
    GalaxyAvatarModule,
    ActivityCardComponent,
    RouterLink,
  ],
})
export class CrmActivityRecordChangeSalespersonUpdatedComponent extends CrmActivityRecordChangeBaseComponent {
  private readonly salesperson$ = combineLatest([this.changeLogData$, this.config.namespace$]).pipe(
    switchMap(([changeLogData, namespace]) => {
      const salesPersonSubId = changeLogData
        ? this.getStringFieldData(changeLogData, StandardIds.CompanyPrimarySalespersonID)
        : '';
      return salesPersonSubId
        ? this.salespersonService
            .getSalespersonBySubjectId(namespace || '', salesPersonSubId)
            .pipe(catchError(() => of(null)))
        : of(null);
    }),
  );

  private readonly salespersonNotAssignedTranslation$ = this.association$.pipe(
    switchMap((association) => {
      const objectType = association?.crmObjectType as ObjectType;
      return this.translateForCrmObjectService.getTranslationForCrmObject(
        objectType || 'Company',
        'RECORD_CHANGE_ACTIVITY.IMPORTANT_FIELD_UPDATED_SALESPERSON_NOT_ASSIGNED',
      );
    }),
  );

  protected override readonly contentText$ = combineLatest([
    this.association$,
    this.salesperson$,
    this.changeLogData$,
    this.salespersonNotAssignedTranslation$,
  ]).pipe(
    map(([association, salesPersonData, changeLogData, salespersonNotAssignedTranslation]) => {
      const changeLogSalespersonId = changeLogData
        ? this.getStringFieldData(changeLogData, StandardIds.CompanyPrimarySalespersonID) || ''
        : '';
      const computedObjectType = association?.crmObjectType;

      if (!computedObjectType) return '';

      const salespersonKey = this.translate.instant('ACTIVITY.RECORD_CHANGE.SALESPERSON');

      if (salesPersonData === null && changeLogSalespersonId) {
        const salespersonUnknown = this.translate.instant(
          'ACTIVITY.RECORD_CHANGE.IMPORTANT_FIELD_UPDATED_SALESPERSON_UNKNOWN',
        );
        return `${salespersonKey} ${salespersonUnknown}`.trim();
      }

      if (salesPersonData) {
        const salespersonFullName = [salesPersonData.firstName ?? '', salesPersonData.lastName ?? '']
          .map((name) => name.trim())
          .join(' ');

        const emailText = salesPersonData.email ? ` (${salesPersonData.email})` : '';

        return `${salespersonKey} ${salespersonFullName}${emailText}`.trim();
      }
      return `${salespersonKey} ${salespersonNotAssignedTranslation}`.trim();
    }),
  );

  constructor(
    fieldService: CrmFieldService,
    translateForCrmObjectService: TranslateForCrmObjectService,
    changeLogService: CRMChangeLogApiService,
    objectService: CrmObjectService,
    objectDisplayService: CrmObjectDisplayService,
    translate: TranslateService,
    activityAssociationService: ActivityAssociationService,
    @Inject(CrmInjectionToken) config: CrmDependencies,
    @Inject(SalespersonService) private readonly salespersonService: SalesSdk,
  ) {
    super(
      fieldService,
      translateForCrmObjectService,
      changeLogService,
      objectService,
      objectDisplayService,
      translate,
      activityAssociationService,
      config,
    );
  }
}
